import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect } from "react";
import { 
  LayoutDashboard, 
  FileText, 
  Package, 
  ShoppingCart, 
  DollarSign,
  Leaf,
  Link2,
  ClipboardList,
  PieChart,
  ScanBarcode,
  Menu,
  X
} from "lucide-react";
import Dashboard from "./pages/Dashboard";
import Invoices from "./pages/Invoices";
import Stock from "./pages/Stock";
import Sales from "./pages/Sales";
import Pricing from "./pages/Pricing";
import Reconciliation from "./pages/Reconciliation";
import Inventory from "./pages/Inventory";
import PnL from "./pages/PnL";
import WeeklyInventory from "./pages/WeeklyInventory";

const Sidebar = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const links = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/invoices", icon: FileText, label: "Notas Fiscais" },
    { to: "/stock", icon: Package, label: "Estoque" },
    { to: "/inventory", icon: ClipboardList, label: "Inventário" },
    { to: "/weekly-inventory", icon: ScanBarcode, label: "Inv. Semanal" },
    { to: "/sales", icon: ShoppingCart, label: "Vendas" },
    { to: "/pricing", icon: DollarSign, label: "Precificação" },
    { to: "/reconciliation", icon: Link2, label: "Reconciliação" },
    { to: "/pnl", icon: PieChart, label: "P&L" },
  ];

  // Close sidebar on route change (mobile)
  useEffect(() => { onClose(); }, [location.pathname]); // eslint-disable-line

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`sidebar fixed left-0 top-0 h-screen w-64 flex flex-col z-50 transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-sm flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Sistema de Gestão
              </h1>
              <p className="text-white/60 text-xs">iFreshMarket</p>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden p-1 text-white/60 hover:text-white" data-testid="close-sidebar-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="flex-1 py-4 overflow-y-auto">
          {links.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${to.slice(1)}`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-1.5 opacity-60">
              <span className="text-white/50 text-[10px]">powered by</span>
            </div>
            <a href="https://www.crowdsa.com.br" target="_blank" rel="noopener noreferrer">
              <img src="/images/crowdsa-logo.png" alt="CrowdSA" className="h-5 opacity-70 hover:opacity-100 transition-opacity cursor-pointer" />
            </a>
            <p className="text-white/30 text-[9px] text-center leading-tight">
              CrowdSA® — All Rights Reserved 2026
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-border flex items-center px-4 z-30">
        <button onClick={() => setSidebarOpen(true)} className="p-1.5 hover:bg-muted rounded" data-testid="open-sidebar-btn">
          <Menu className="w-5 h-5" />
        </button>
        <span className="ml-3 font-bold text-sm" style={{ fontFamily: 'Manrope' }}>Sistema de Gestão - iFreshMarket</span>
      </div>

      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/stock" element={<Stock />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/reconciliation" element={<Reconciliation />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/weekly-inventory" element={<WeeklyInventory />} />
          <Route path="/pnl" element={<PnL />} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
